import React from 'react';
import { observer } from 'mobx-react';
import { UrlRedirectionFormVM } from './url-redirection-form.viewmodel';
import { URL_REDIRECTION_FORM_CONFIG, UrlRedirectionFieldKey } from './url-redirection-form.config';
import {
    REDIRECTION_URL_TEXT,
    URL_REDIRECTION_DISABLED_TEXT,
    URL_REDIRECTION_ENABLED_TEXT,
    URL_REDIRECTION_INFO_TEXT,
} from './url-redirection-form.texts';
import { FormField } from '../../../../../global/form/form-field/form-field';
import { FormFieldError } from '../../../../../global/form/form-field-input/form-field-error';
import { OEToggle } from '../../../../../global/oe-toggle/oe-toggle';
import { InfoPopover } from '../../../../../global/info-popover/info-popover';
import { CommonPopupBodyFooterLayout } from '../../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../../global/sub-window/common/common-close-sub-window-buttons';
import DefaultButton from '../../../../../global/default-button/default-button';
import { APPLY_AND_CLOSE_TEXT, APPLY_TEXT, CANCEL_TEXT } from '../../../../../../constants/common-labels.constants';
import './url-redirection-form.presentation.scss';

interface IProps {
    vm: UrlRedirectionFormVM;
}

const UrlRedirectionFormBody = observer(({ vm }: IProps): JSX.Element => {
    const urlField = vm.getUrlField();

    return (
        <div className='url-redirection-form'>
            <div className='url-redirection-form__toggle m-bottom-15px'>
                <OEToggle
                    isSelected={vm.enabled}
                    onClick={vm.toggleEnabled}
                    isDisabled={vm.saving}
                    dataTestId='url-redirection-toggle'
                />
                <span className='url-redirection-form__toggle-label'>
                    {vm.intl.formatMessage(vm.enabled ? URL_REDIRECTION_ENABLED_TEXT : URL_REDIRECTION_DISABLED_TEXT)}
                </span>
                <InfoPopover>
                    <div>{vm.intl.formatMessage(URL_REDIRECTION_INFO_TEXT)}</div>
                </InfoPopover>
            </div>
            <FormField label={vm.intl.formatMessage(REDIRECTION_URL_TEXT)}>
                <input
                    type='text'
                    className='oe-form-field__control'
                    value={urlField.value as string}
                    onChange={({ target: { value } }) => urlField.setValue(value)}
                    onBlur={urlField.setTouched}
                    disabled={vm.saving || !vm.enabled}
                />
                <FormFieldError
                    field={urlField}
                    labelDescriptor={URL_REDIRECTION_FORM_CONFIG[UrlRedirectionFieldKey.URL].labelDescriptor}
                />
            </FormField>
        </div>
    );
});

const UrlRedirectionFormFooter = observer(({ vm }: IProps): JSX.Element => (
    <>
        {vm.saving && (
            <span className='oe-popup-layout__footer-status-area'>
                {vm.intl.formatMessage({
                    id: 'oe.customFields.dataIsBeingSaved',
                    description: 'Preloader message text',
                    defaultMessage: 'Data is being saved, please wait{suffix}',
                }, { suffix: '...' })}
            </span>
        )}
        <div className='buttons-block buttons-block--from-right'>
            <DefaultCloseSubWindowButton
                text={vm.intl.formatMessage(APPLY_AND_CLOSE_TEXT)}
                modifier={['fat', 'confirm-safe']}
                onClick={vm.submitForm}
                disabled={vm.saving}
            />
            <DefaultButton
                text={vm.intl.formatMessage(APPLY_TEXT)}
                modifier={['fat', 'confirm-safe']}
                onClick={vm.submitForm}
                disabled={vm.saving}
            />
            <DefaultCloseSubWindowButton
                text={vm.intl.formatMessage(CANCEL_TEXT)}
            />
        </div>
    </>
));

const UrlRedirectionFormPresentation = observer(({ vm }: IProps): JSX.Element => (
    <CommonPopupBodyFooterLayout
        className='oe-form'
        body={<UrlRedirectionFormBody vm={vm} />}
        footer={<UrlRedirectionFormFooter vm={vm} />}
    />
));

export { UrlRedirectionFormPresentation, UrlRedirectionFormBody, UrlRedirectionFormFooter };